import { FlaskConical } from 'lucide-react'
import { useState } from 'react'
import { config } from '../config'

/**
 * The honest label on prototype mode.
 *
 * Non-negotiable for the same reason `PhasePlaceholderPage` says "not built": a
 * convincing screen full of fabricated data is worse than an empty one, because
 * somebody will demo it as working software. This states plainly that the data is
 * fixture data and where it comes from.
 *
 * Dismissible, but only for the session, and it never claims to be anything else.
 * A banner that cannot be dismissed gets screenshotted out of demos; one that
 * remembers a dismissal across reloads would need storage the ESLint rule bans
 * and would eventually hide the disclosure from someone who never dismissed it.
 *
 * Note the contrast with the AI-disclosure labels in @insightai/ai-patterns,
 * which are genuinely non-suppressible: those satisfy a compliance AC (AC-34,
 * AC-40), this is a development affordance. Conflating the two would cheapen the
 * ones that matter.
 */
export function PrototypeBanner() {
  const [dismissed, setDismissed] = useState(false)
  if (!config.prototypeMode || dismissed) return null

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-state-warning-border bg-state-warning-surface px-4 py-1.5 text-caption text-state-warning">
      <FlaskConical aria-hidden className="size-3.5 shrink-0" />
      <span>
        <strong>Prototype mode.</strong> Briefs, TOSCA, structuring, assumptions, design, client data
        and the review queue read from <code className="font-mono">mock_data/</code>, not a backend.
        Changes persist for this browser session only.
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ms-auto rounded-sm px-1.5 underline underline-offset-2 hover:text-text-primary focus-visible:ring-2 focus-visible:ring-border-focus"
      >
        Hide for this session
      </button>
    </div>
  )
}
